import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import {
  api,
  ProjectShareLinkAnalyticsResponse,
  ShareLinkAnalyticsQuery,
  StatisticsProject,
} from '../../services/api';
import ShareLinksAnalyticsTab from '../admin/share-links-analytics/ShareLinksAnalyticsTab';
import ProjectActivitiesTab from '../others/ProjectActivitiesTab';
import { formatInt } from '../admin/share-links-analytics/shareLinksAnalyticsModel';
import StatisticsDialog from './StatisticsDialog';

const slug = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'project';

const projectSubline = (p: StatisticsProject) =>
  [p.role, `${formatInt(p.links)} ${p.links === 1 ? 'link' : 'links'} shared`].filter(Boolean).join(' · ');

/**
 * The Shareable Links dashboard over one project: every agent's links for it,
 * and the leads they brought in. The list holds the projects the user heads
 * (Head / Deputy Head, or IC where there is neither); admins and Project Dept
 * get every project that has been shared.
 */
const ProjectShareLinksTab: React.FC = () => {
  const [projects, setProjects] = useState<StatisticsProject[] | null>(null);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [selected, setSelected] = useState<StatisticsProject | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.getStatisticsProjects()
      .then((res) => {
        if (cancelled) return;
        setProjects(res.projects);
        // Open on the most-shared project so there is something to read straight away.
        setSelected(res.projects[0] ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        setProjects([]);
        setProjectsError(err instanceof Error ? err.message : 'Failed to load projects');
      });
    return () => { cancelled = true; };
  }, []);

  const projectId = selected?.projectId;
  const fetchAnalytics = useCallback(
    (query: ShareLinkAnalyticsQuery) => api.getProjectShareLinkAnalytics({ ...query, projectId: projectId as string }),
    [projectId],
  );

  if (projects === null) {
    return <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
  }
  if (projectsError) return <Alert severity="error">{projectsError}</Alert>;
  if (projects.length === 0) {
    return (
      <Alert severity="info">
        You are not Head, Deputy Head or IC of any active project, so there are no project statistics to show.
      </Alert>
    );
  }

  return (
    <ShareLinksAnalyticsTab<ProjectShareLinkAnalyticsResponse>
      fetchAnalytics={projectId ? fetchAnalytics : null}
      singleProject
      toolbarStart={(
        <Autocomplete
          size="small"
          options={projects}
          value={selected}
          onChange={(_, value) => setSelected(value)}
          getOptionLabel={(p) => p.projectName}
          isOptionEqualToValue={(a, b) => a.projectId === b.projectId}
          renderOption={(props, p) => (
            <Box component="li" {...props} key={p.projectId}>
              <Box>
                <Typography variant="body2">{p.projectName}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                  {projectSubline(p)}
                </Typography>
              </Box>
            </Box>
          )}
          sx={{ width: 320 }}
          renderInput={(params) => <TextField {...params} label="Project" placeholder="Choose a project" />}
        />
      )}
      emptyState={(
        <Typography variant="body2" sx={{ py: 6, textAlign: 'center', color: 'rgba(var(--color-text-rgb), 0.6)' }}>
          Choose a project to see how its links are being shared.
        </Typography>
      )}
      scopeCaption={(data) => data.scope.projectName}
      exportScope={(data) => ({ label: `Project: ${data.scope.projectName}`, fileTag: slug(data.scope.projectName) })}
    />
  );
};

interface ProjectHeadStatisticsDialogProps {
  open: boolean;
  onClose: () => void;
  /** Unlocks the per-agent drill-in on Project Activities. */
  isAdmin?: boolean;
}

/**
 * Project Head → Statistics: Project Activities first, then Shareable Links.
 * The server scopes Shareable Links to the projects the user heads (admins and
 * Project Dept see every project).
 */
const ProjectHeadStatisticsDialog: React.FC<ProjectHeadStatisticsDialogProps> = ({ open, onClose, isAdmin = false }) => (
  <StatisticsDialog
    open={open}
    onClose={onClose}
    title="Statistics"
    subtitle="Activity on the projects you head, how they are being shared, and the leads they bring in."
    tabs={[
      { value: 'activities', label: 'Project Activities', render: () => <ProjectActivitiesTab isAdmin={isAdmin} /> },
      { value: 'shareLinks', label: 'Shareable Links', render: () => <ProjectShareLinksTab /> },
    ]}
  />
);

export default ProjectHeadStatisticsDialog;
